<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

interface ConditionsData {
  house?: {
    temperature?: number
    humidity?: number
    pressure?: number
  }
  weather?: {
    temperature?: number
    humidity?: number
    wind_speed?: number
    wind_direction?: string
    conditions?: string
  }
  power?: {
    battery_voltage?: number
    battery_capacity?: number
    pv_power?: number
    load_power?: number
    grid_status?: string
  }
  thermostat?: {
    outdoor_temperature?: number
    mode?: string
    setpoint?: number
  }
  updated_at?: string
}

const conditions = ref<ConditionsData | null>(null)
const loading = ref(true)
const error = ref('')
const lastUpdated = ref<Date | null>(null)

// Calendar API URL
const API_URL = import.meta.env.VITE_API_URL || ''

let refreshInterval: number | null = null

async function fetchData() {
  try {
    const response = await fetch(`${API_URL}/api/conditions`)

    if (!response.ok) {
      if (response.status === 404) {
        error.value = 'No data available - cabin may be offline'
      } else {
        error.value = 'Unable to fetch cabin data'
      }
      loading.value = false
      return
    }

    const data = await response.json()
    conditions.value = data
    lastUpdated.value = data.updated_at ? new Date(data.updated_at) : new Date()
    error.value = ''
  } catch (e) {
    error.value = 'Unable to fetch cabin data'
    console.error('Fetch error:', e)
  } finally {
    loading.value = false
  }
}

function celsiusToFahrenheit(c: number | null): string {
  if (c === null) return '--'
  return `${Math.round(c * 9/5 + 32)}°F`
}

function formatTemp(c: number | null): string {
  if (c === null) return '--'
  return `${Math.round(c)}°C / ${Math.round(c * 9/5 + 32)}°F`
}

onMounted(() => {
  fetchData()
  // Refresh every 5 minutes
  refreshInterval = window.setInterval(fetchData, 5 * 60 * 1000)
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})
</script>

<template>
  <div class="max-w-4xl mx-auto p-4 md:p-6">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-gray-800">Current Conditions</h1>
      <button
        @click="fetchData"
        class="text-sm text-primary-600 hover:text-primary-800"
        :disabled="loading"
      >
        {{ loading ? 'Updating...' : 'Refresh' }}
      </button>
    </div>

    <!-- Error State -->
    <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <p class="text-red-700">{{ error }}</p>
      <p class="text-sm text-red-600 mt-1">The cabin may be offline or unreachable.</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !conditions" class="text-center py-12">
      <div class="text-4xl mb-4">🏔️</div>
      <p class="text-gray-600">Loading cabin conditions...</p>
    </div>

    <!-- Data Cards -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Indoor Conditions -->
      <div class="card">
        <h2 class="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <span>🏠</span> Inside the Cabin
        </h2>
        <div class="space-y-4">
          <div class="flex justify-between items-center">
            <span class="text-gray-600">Temperature</span>
            <span class="text-2xl font-bold text-gray-800">
              {{ conditions?.house?.temperature != null ? formatTemp(conditions.house.temperature) : '--' }}
            </span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-gray-600">Humidity</span>
            <span class="text-xl font-semibold text-gray-800">
              {{ conditions?.house?.humidity != null ? `${Math.round(conditions.house.humidity)}%` : '--' }}
            </span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-gray-600">Pressure</span>
            <span class="text-xl font-semibold text-gray-800">
              {{ conditions?.house?.pressure != null ? `${Math.round(conditions.house.pressure)} hPa` : '--' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Outdoor Conditions -->
      <div class="card">
        <h2 class="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <span>🌲</span> Outside
        </h2>
        <div class="space-y-4">
          <div class="flex justify-between items-center">
            <span class="text-gray-600">Temperature</span>
            <span class="text-2xl font-bold text-gray-800">
              {{ conditions?.thermostat?.outdoor_temperature != null
                 ? formatTemp(conditions.thermostat.outdoor_temperature)
                 : (conditions?.weather?.temperature != null ? formatTemp(conditions.weather.temperature) : '--') }}
            </span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-gray-600">Humidity</span>
            <span class="text-xl font-semibold text-gray-800">
              {{ conditions?.weather?.humidity != null ? `${Math.round(conditions.weather.humidity)}%` : '--' }}
            </span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-gray-600">Wind</span>
            <span class="text-xl font-semibold text-gray-800">
              {{ conditions?.weather?.wind_speed != null ? `${Math.round(conditions.weather.wind_speed)} mph` : '--' }}
              {{ conditions?.weather?.wind_direction || '' }}
            </span>
          </div>
          <div v-if="conditions?.weather?.conditions" class="flex justify-between items-center">
            <span class="text-gray-600">Conditions</span>
            <span class="text-lg text-gray-800">{{ conditions.weather.conditions }}</span>
          </div>
        </div>
      </div>

      <!-- Thermostat Status -->
      <div v-if="conditions?.thermostat" class="card">
        <h2 class="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <span>🌡️</span> Thermostat
        </h2>
        <div class="space-y-4">
          <div class="flex justify-between items-center">
            <span class="text-gray-600">Mode</span>
            <span class="text-xl font-bold" :class="{
              'text-green-600': conditions.thermostat.mode === 'HOME',
              'text-blue-600': conditions.thermostat.mode === 'AWAY',
              'text-purple-600': conditions.thermostat.mode === 'SLEEP',
              'text-orange-600': conditions.thermostat.mode === 'BOOST',
            }">
              {{ conditions.thermostat.mode || '--' }}
            </span>
          </div>
          <div v-if="conditions.thermostat.setpoint" class="flex justify-between items-center">
            <span class="text-gray-600">Setpoint</span>
            <span class="text-xl font-semibold text-gray-800">
              {{ formatTemp(conditions.thermostat.setpoint) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Power Status -->
      <div class="card" :class="conditions?.thermostat ? '' : 'md:col-span-2'">
        <h2 class="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <span>⚡</span> Power System
        </h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="text-center p-4 bg-gray-50 rounded-lg">
            <div class="text-sm text-gray-600 mb-1">Battery</div>
            <div class="text-2xl font-bold text-gray-800">
              {{ conditions?.power?.battery_capacity != null ? `${Math.round(conditions.power.battery_capacity)}%` : '--' }}
            </div>
            <div class="text-sm text-gray-500">
              {{ conditions?.power?.battery_voltage != null ? `${conditions.power.battery_voltage.toFixed(1)}V` : '' }}
            </div>
          </div>
          <div class="text-center p-4 bg-gray-50 rounded-lg">
            <div class="text-sm text-gray-600 mb-1">Solar</div>
            <div class="text-2xl font-bold text-yellow-600">
              {{ conditions?.power?.pv_power != null ? `${Math.round(conditions.power.pv_power)}W` : '--' }}
            </div>
          </div>
          <div class="text-center p-4 bg-gray-50 rounded-lg">
            <div class="text-sm text-gray-600 mb-1">Load</div>
            <div class="text-2xl font-bold text-blue-600">
              {{ conditions?.power?.load_power != null ? `${Math.round(conditions.power.load_power)}W` : '--' }}
            </div>
          </div>
          <div class="text-center p-4 bg-gray-50 rounded-lg">
            <div class="text-sm text-gray-600 mb-1">Grid</div>
            <div class="text-xl font-bold" :class="conditions?.power?.grid_status === 'Connected' ? 'text-green-600' : 'text-gray-600'">
              {{ conditions?.power?.grid_status || 'Off-Grid' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Last Updated -->
    <p v-if="lastUpdated" class="text-center text-sm text-gray-500 mt-6">
      Last updated: {{ lastUpdated.toLocaleTimeString() }}
    </p>

    <!-- Camera Placeholder -->
    <div class="card mt-6">
      <h2 class="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
        <span>📷</span> Live Cameras
      </h2>
      <div class="bg-gray-100 rounded-lg p-8 text-center">
        <div class="text-4xl mb-2">🚧</div>
        <p class="text-gray-600">Camera integration coming soon</p>
        <p class="text-sm text-gray-500">IP cameras will show live views of the driveway and property</p>
      </div>
    </div>
  </div>
</template>
